/**
 * The block definitions a level's ids refer to.
 *
 * A level stores a block id, a footprint origin and a rotation and nothing about the
 * block itself (§10 of develop/DungeonBlocks.md), so the client cannot draw one
 * without the catalogue the admin editor authors. Same document and same route the
 * editor's `BlockStore` reads -- one catalogue, one place it lives -- but read-only
 * here: the client never writes a definition.
 *
 * A catalogue that will not load is a warning and an empty one rather than a throw,
 * because the alternative is a blank page: a level whose blocks are all unknown
 * resolves to nothing and draws nothing, which is visibly wrong in the right way.
 */
import type { BlockCatalogue } from '@dungeon-builder/shared';

const CATALOGUE_URL = '/assets/modules/blocks.json';

interface CatalogueDocument {
  formatVersion: number;
  blocks: BlockCatalogue;
}

export async function loadBlockCatalogue(): Promise<BlockCatalogue> {
  try {
    const response = await fetch(CATALOGUE_URL);
    if (!response.ok) {
      console.warn(`[blocks] ${CATALOGUE_URL} answered ${response.status}; no blocks to draw.`);
      return {};
    }
    const parsed = (await response.json()) as Partial<CatalogueDocument>;
    const blocks = parsed.blocks;
    if (!blocks || typeof blocks !== 'object' || Array.isArray(blocks)) {
      console.warn(`[blocks] ${CATALOGUE_URL} holds no "blocks" object; no blocks to draw.`);
      return {};
    }
    // Trusted as far as the editor's own legality goes: Define is what writes this file
    // and it enforces `isAllowedFootprint` and `isPortOnBoundary` on every edit.
    return blocks;
  } catch (error) {
    console.warn(`[blocks] could not read ${CATALOGUE_URL}`, error);
    return {};
  }
}
